/* eslint-disable no-unused-vars */
import { end } from '@popperjs/core';
import React, { createContext, useContext, useState } from 'react';
import componentsType from '../components/Component/ComponentType';
import DialogConditionDTO from '../dto/DialogConditionDTO';
import DialogDTO from '../dto/DialogDTO';
import DialogNextStepDTO from '../dto/DialogNextStepDTO';
import DialogOutputDTO from '../dto/DialogOutputDTO';
import DialogSlotsDTO from '../dto/DialogSlotsDTO';
import endPoint from '../service/Service';
import { removePropertyRecursive, compare as compareObject } from '../utils/functions';

import SaveContext from './SaveContext';

const WATSON_DEFAULT_PAGE_SIZE = 500;

export const Context = createContext({
  initialize: () => { },
  initialized: false,
  intents: {},
  entities: {},
  dialogs: [],
  fetchDialogs: (successCallback, failureCallback) => { },
  createDialog: (dialogId, sourceNode, successCallback) => { },
  saveDialog: (dialog, recreate, successCallback, failureCallback) => { },
  deleteDialog: (dialogId, successCallback) => { },
  fetchIntents: (successCallback, failureCallback) => { },
  fetchEntities: (successCallback, failureCallback) => { },
  saveIntentOrEntity: () => { },
  deleteIntentsOrEntities: () => { },
  reset: () => { },
});

export const ContextProviderWatson = ({ children }) => {
  const saveContext = useContext(SaveContext);

  const [initialized, setInitialized] = useState(false);
  const [watsonConfig, setWatsonConfig] = useState(null);
  const [dialogs, setDialogs] = useState(null);
  const [intents, setIntents] = useState(null);
  const [entities, setEntities] = useState(null);

  const reset = () => {
    setInitialized(false);
    setWatsonConfig(null);
    setDialogs(null);
    setIntents(null);
    setEntities(null);
  };

  const initialize = (config) => {
    const newConfig = {
      ...config,
      size: WATSON_DEFAULT_PAGE_SIZE,
    };
    delete newConfig.workspaceId;
    const params = new URLSearchParams(newConfig);
    reset();
    setWatsonConfig({
      params,
      workspaceId: config.workspaceId,
      workspaceUrl: `watson/assistant/workspaces/${config.workspaceId}`,
    });
    setInitialized(true);
  };

  const fetchDialogs = (successCallback, failureCallback) => {
    endPoint.get(`${watsonConfig.workspaceUrl}/dialogs?${watsonConfig.params}`)
      .then((response) => {
        if (response.status === 200) {
          let fetchedDialogs = response.data.data;
          fetchedDialogs = DialogDTO.buildDialogFlow(fetchedDialogs);
          setDialogs(fetchedDialogs);
          if (successCallback) successCallback();
        }
      }, (response) => {
        if (failureCallback) failureCallback(response);
      });
  };

  const fetchIntents = (successCallback, failureCallback) => {
    endPoint.get(`${watsonConfig.workspaceUrl}/intents?${watsonConfig.params}`)
      .then((response) => {
        if (response.status === 200) {
          let fetchedIntents = response.data.data;
          fetchedIntents = fetchedIntents.sort((a, b) => compareObject(a, b, 'name', true));
          fetchedIntents = fetchedIntents.reduce((acc, item) => {
            acc[item.name] = { ...item };
            return acc;
          }, {});

          setIntents(fetchedIntents);
          if (successCallback) successCallback();
        }
      }, (response) => {
        if (failureCallback) failureCallback(response);
      });
  };

  const fetchEntities = (successCallback, failureCallback) => {
    endPoint.get(`${watsonConfig.workspaceUrl}/entities?${watsonConfig.params}`)
      .then((response) => {
        if (response.status === 200) {
          let fetchedEntities = response.data.data;
          fetchedEntities = fetchedEntities.filter((e) => !e.name.startsWith('sys-'));
          fetchedEntities = fetchedEntities.sort((a, b) => compareObject(a, b, 'name', true));
          fetchedEntities = fetchedEntities.reduce((acc, item) => {
            acc[item.name] = { ...item };
            return acc;
          }, {});

          setEntities(fetchedEntities);
          if (successCallback) successCallback();
        }
      }, (response) => {
        if (failureCallback) failureCallback(response);
      });
  };

  const saveServerErroHandler = (error = null, errorCallback = null) => {
    saveContext.setSavingStatus({
      ...saveContext,
      isSaving: false,
      isSuccessfulSave: false,
      savingInWatson: true,
    });
    if (errorCallback) errorCallback();
  };

  const saveIntentOrEntity = (type, obj, create, updateObjAfterSave) => {
    const object = {
      ...obj,
      workspaceId: watsonConfig.workspaceId,
    };

    if (type === 'entities') {
      object.fuzzyMatch = object.fuzzyMatch || true;
    }

    let promisse = null;
    if (create) {
      const url = `${watsonConfig.workspaceUrl}/${type}?${watsonConfig.params}`;
      promisse = endPoint.post(url, object);
    } else {
      const url = `${watsonConfig.workspaceUrl}/${type}/${object.name}?${watsonConfig.params}`;
      promisse = endPoint.put(url, object);
    }

    saveContext.beginSaving(true);
    promisse.then((response) => {
      if (response.status === 200 || response.status === 201) {
        const objectsInContext = type === 'intents' ? { ...intents } : { ...entities };
        const setObjectsInContext = type === 'intents' ? setIntents : setEntities;
        const objectReturned = response.data;
        if (!create) {
          const oldName = object.name;
          delete objectsInContext[oldName];
        }
        objectsInContext[objectReturned.name] = objectReturned;
        setObjectsInContext(objectsInContext);
        updateObjAfterSave(objectReturned);

        saveContext.finishSaving(true);
      }
    }, saveServerErroHandler);
  };

  const deleteIntentsOrEntities = (type, objectsToDelete, successCallback) => {
    const objectsInContext = type === 'intents' ? { ...intents } : { ...entities };
    const setObjectsInContext = type === 'intents' ? setIntents : setEntities;

    const doDelete = (objToDelete) => {
      const objectName = objToDelete.pop();
      const url = `${watsonConfig.workspaceUrl}/${type}/${objectName}?${watsonConfig.params}`;
      endPoint.delete(url).then((response) => {
        if (response.status === 204) {
          delete objectsInContext[objectName];
          if (objToDelete.length > 0) doDelete(objToDelete);
          else {
            if (successCallback) successCallback();
            setObjectsInContext(objectsInContext);
            saveContext.finishSaving(true);
          }
        }
      }, saveServerErroHandler);
    };

    saveContext.beginSaving(true);
    doDelete(objectsToDelete);
  };

  const createDialog = (node, nodeFromId, successCallback) => {
    const previous = dialogs[nodeFromId] || {
      parent: undefined,
      previousSibling: undefined,
    };

    const dialog = new DialogDTO();
    dialog.id = node.id;
    dialog.workspaceId = watsonConfig.workspaceId;
    dialog.title = ' ';

    const componentTypeId = node.typeElement.id;
    switch (componentTypeId) {
      case (componentsType.WATSON_DIALOG.id): {
        dialog.type = 'standard';
        dialog.parent = previous.parent;
        dialog.previousSibling = previous.id;
        break;
      }
      case (componentsType.WATSON_DIALOG_IN_FOLDER.id): {
        dialog.type = 'standard';
        dialog.parent = previous.id;
        dialog.previousSibling = undefined;
        break;
      }
      case (componentsType.WATSON_DIALOG_FOLDER.id): {
        dialog.type = 'folder';
        dialog.parent = previous.parent;
        dialog.previousSibling = previous.id;
        break;
      }
      case (componentsType.WATSON_DIALOG_FOLDER_IN_FOLDER.id): {
        dialog.type = 'folder';
        dialog.parent = previous.id;
        dialog.previousSibling = undefined;
        break;
      }

      default: break;
    }

    saveContext.beginSaving(true);
    endPoint.post(`${watsonConfig.workspaceUrl}/dialogs?${watsonConfig.params}`, dialog)
      .then((response) => {
        if (response.status === 201) {
          fetchDialogs(() => {
            saveContext.finishSaving(true);
            successCallback(response.data);
          });
        }
      }, saveServerErroHandler);
  };

  const objectsAreEquals = (objA, objB, attrDoesntCompare) => {
    if ((!objA && !!objB) || (!!objA && !objB)) return false;

    const objAToCompare = JSON.parse(JSON.stringify(objA));
    const objBToCompare = JSON.parse(JSON.stringify(objB));
    removePropertyRecursive(objAToCompare, attrDoesntCompare);
    removePropertyRecursive(objBToCompare, attrDoesntCompare);

    if (JSON.stringify(objAToCompare) === JSON.stringify(objBToCompare)) {
      return true;
    }
    return false;
  };

  const saveDialog = (dialog, recreate, successCallback, failureCallback) => {
    const contexDialog = dialogs[dialog.id];

    const enabledChannelConditions = DialogConditionDTO
      .buildFromEnabledChannels(dialog.enabledChannels);

    const conditions = DialogConditionDTO.parseList(
      dialog.dialogConditions
        ? dialog.dialogConditions.concat(enabledChannelConditions)
        : enabledChannelConditions,
    );

    const changedDialog = new DialogDTO({
      ...contexDialog,
      conditions,
      previousSibling: dialog.previousSibling,
      parent: dialog.parent,
      slots: null,
    });

    changedDialog.title = dialog.title === '' ? ' ' : dialog.title;

    const outputs = DialogOutputDTO.parseList(dialog.outputs);
    if (outputs) changedDialog.outputs = outputs;

    const nextStep = dialog.nextStep ? new DialogNextStepDTO(dialog.nextStep).asOutput() : null;
    if (nextStep) changedDialog.nextStep = nextStep;

    if (contexDialog.slots) {
      changedDialog.slotsToRemove = contexDialog.slots.map((s) => ({ id: s.id }));
    }
    if (dialog.slots && !objectsAreEquals(dialog.slots, contexDialog.slots, ['id'])) {
      changedDialog.slotsToAdd = DialogSlotsDTO.parseList(dialog.slots);
    } else if (changedDialog.type !== 'folder') {
      changedDialog.type = 'standard';
    }

    if (objectsAreEquals(contexDialog, changedDialog, ['id', 'order', 'dialogConditions'])) {
      if (successCallback) successCallback();
      return null;
    }

    const payload = {
      ...changedDialog,
      id: dialog.id,
      workspaceId: watsonConfig.workspaceId,
    };

    saveContext.beginSaving(true);

    if (recreate) {
      endPoint.delete(`${watsonConfig.workspaceUrl}/dialogs/${dialog.id}?${watsonConfig.params}`).then(
        (response) => {
          if (response.status === 204) {
            endPoint.post(`${watsonConfig.workspaceUrl}/dialogs?${watsonConfig.params}`, payload).then((responseCreated) => {
              if (responseCreated.status === 201) {
                fetchDialogs(() => {
                  saveContext.finishSaving(true);
                  if (successCallback) successCallback();
                });
              }
            }, (error) => saveServerErroHandler(error, failureCallback));
          }
        }, (error) => saveServerErroHandler(error, failureCallback),
      );
    } else {
      endPoint.put(`${watsonConfig.workspaceUrl}/dialogs/anything?${watsonConfig.params}`, payload).then(
        (response) => {
          if (response.status === 200) {
            fetchDialogs(() => {
              saveContext.finishSaving(true);
              if (successCallback) successCallback();
            });
          }
        },
        (error) => saveServerErroHandler(error, failureCallback),
      );
    }
  };

  const deleteDialog = (dialogId, successCallback, failureCallback) => {
    const url = `${watsonConfig.workspaceUrl}/dialogs/${dialogId}?${watsonConfig.params}`;
    saveContext.beginSaving(true);
    endPoint.delete(url).then((response) => {
      if (response.status === 204) {
        fetchDialogs(() => {
          saveContext.finishSaving(true);
          successCallback();
        });
      }
    }, (error) => saveServerErroHandler(error, failureCallback));
  };

  return (
    <Context.Provider value={{
      initialize,
      initialized,
      dialogs,
      fetchDialogs,
      saveDialog,
      createDialog,
      deleteDialog,
      intents,
      fetchIntents,
      entities,
      fetchEntities,
      saveIntentOrEntity,
      deleteIntentsOrEntities,
      reset,
    }}
    >
      {children}
    </Context.Provider>
  );
};

export default Context;
